import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { InstallerRow } from '@/types/api'

export function useTicketReplyAttachments() {
  const [files, setFiles] = useState<File[]>([])
  const [pendingInstallers, setPendingInstallers] = useState<InstallerRow[]>([])
  const replyFileIconResetRef = useRef<(() => void) | null>(null)

  const appendFiles = useCallback((incoming: File[] | null) => {
    if (!incoming?.length) return
    setFiles((prev) => {
      const next = [...prev]
      for (const file of incoming) {
        const duplicate = next.some(
          (candidate) =>
            candidate.name === file.name &&
            candidate.size === file.size &&
            candidate.lastModified === file.lastModified,
        )
        if (!duplicate) next.push(file)
      }
      return next
    })
  }, [])

  const removeFileAt = useCallback((index: number) => {
    setFiles((prev) => prev.filter((_, currentIndex) => currentIndex !== index))
  }, [])

  const addPendingInstaller = useCallback((installer: InstallerRow) => {
    setPendingInstallers((prev) =>
      prev.some((pending) => pending.id === installer.id) ? prev : [...prev, installer],
    )
  }, [])

  const removePendingInstallerAt = useCallback((index: number) => {
    setPendingInstallers((prev) => prev.filter((_, currentIndex) => currentIndex !== index))
  }, [])

  const pendingInstallerIds = useMemo(
    () => new Set(pendingInstallers.map((installer) => installer.id)),
    [pendingInstallers],
  )

  const imageBlobUrls = useMemo(
    () => files.map((file) => (file.type.startsWith('image/') ? URL.createObjectURL(file) : '')),
    [files],
  )

  useEffect(() => {
    return () => {
      imageBlobUrls.forEach((url) => {
        if (url) URL.revokeObjectURL(url)
      })
    }
  }, [imageBlobUrls])

  const resetAttachments = useCallback(() => {
    setFiles([])
    setPendingInstallers([])
    replyFileIconResetRef.current?.()
  }, [])

  return {
    addPendingInstaller,
    appendFiles,
    files,
    imageBlobUrls,
    pendingInstallerIds,
    pendingInstallers,
    removeFileAt,
    removePendingInstallerAt,
    resetAttachments,
    replyFileIconResetRef,
  }
}
